import * as outstream from './Outstream'
import { playVideo } from './Outstream'
import { ajax } from './ajax'
import Logger from './Log'

const Log = Logger.for('utils')

/**
 * Checks for multiple instances of same zone Id on page
 * @param zoneId
 * @returns {number} idx starting at 0 of instances
 */
export function instanceIdx (zoneId) {
  const frame = topmostWindowFrame()
  const wv = 'smrtb_' + zoneId + '_idx'

  let v = frame[wv]
  if (v === undefined) {
    v = frame[wv] = 0
  } else {
    v = frame[wv] += 1
  }

  return v
}

/**
 * Renders the given ad into an iframe for the given slot
 * @param ad ad to render
 * @param slot AdSlot to render into (node)
 * @param media the PrebidMedia options which may contain configured player size etc
 * if should attach to document body
 */
export function renderAd (ad, slot, media) {
  switch (ad.mediaType) {
    case 'video':
      if (isVast(ad)) {
        return renderVideo(ad, slot, media)
      } else {
        Log.info('Bid marked as video but detected html markup, rendering as banner')
      }
    // eslint-disable-next-line no-fallthrough
    case 'banner':
      return renderBanner(ad, slot, media)
    case 'native':
      return renderNative(ad, slot, media)
    default:
      throw new Error('Unhandled mediaType ' + ad.mediaType)
  }
}

/**
 * Creates a new iframe for content, applying common styling
 * to remove iframe borders etc
 * @param parent parent to attach frame to
 * @param content content to be written
 * @param width initial width
 * @param height initial height
 * @returns {Promise<frame>} Promise resolves with a reference to the iframe element
 */
export function newContentFrame (parent, content, width, height) {
  return new Promise((resolve, reject) => {
    if (!content || content === '') {
      content = '<html><head></head><body></body></html>'
    }

    const frame = document.createElement('iframe')
    frame.setAttribute('FRAMEBORDER', 0)
    frame.setAttribute('SCROLLING', 'no')
    frame.setAttribute('MARGINHEIGHT', 0)
    frame.setAttribute('MARGINWIDTH', 0)
    frame.setAttribute('TOPMARGIN', 0)
    frame.setAttribute('LEFTMARGIN', 0)
    frame.setAttribute('ALLOWTRANSPARENCY', 'true')
    frame.setAttribute('width', width)
    frame.setAttribute('height', height)
    frame.style.display = 'block'

    let fired = false
    frame.onload = () => {
      if (fired) {
        return Log.debug('Duplicate frame load fire, tisk tisk')
      }

      Log.debug('Iframe load event')

      fired = true
      frame.contentDocument.open()
      frame.contentDocument.write(content)
      frame.contentDocument.close()

      resolve(frame)
    }

    frame.onerror = reject

    parent.appendChild(frame)
  })
}

export function renderBanner (ad, slot, media) {
  const dims = playerDims(media, ad)

  Log.debug('renderBanner ' + dims[0] + 'x' + dims[1])
  return newContentFrame(slot.node(), ad.ad, dims[0], dims[1])
}

export function renderVideo (ad, slot, media) {
  const player = document.createElement('script')
  player.type = 'application/javascript'
  player.src = outstream.PLAYER_SRC

  const p = new Promise((resolve, reject) => {
    player.onload = function () {
      let promise

      if (media.video.contentUrl) {
        promise = Promise.resolve({ media: media.video.contentUrl })
      } else if (media.video.rss_url) {
        promise = rssReader(media.video.rss_url)
      } else {
        promise = Promise.resolve()
      }

      promise.then(content => {
        if (content) {
          Log.debug('Had content for video')
          Log.debug(JSON.stringify(content))
        }

        playVideo(ad, slot, media, content).then(resolve).catch(reject)
      })
    }
  })

  document.body.appendChild(player)

  return p
}

export async function renderNative (ad, slot, media) {
  Log.debug('Render native ad')
  const c = document.createElement('div')
  c.style = 'background-color: #F0F0F0; display: flex; flex-wrap: wrap; margin-bottom: 10px; max-width: 1000px; font-family: Arial; align-items: center;'

  const n = ad.native
  const imgc = document.createElement('a')
  imgc.style = 'flex: 1 1 85px;'
  imgc.href = n.clickUrl
  imgc.target = '_blank'
  const img = document.createElement('img')
  img.src = n.image.url
  img.style = 'display: block; width: 100%; max-height: 150px; object-position: center; object-fit: cover;'
  imgc.append(img)
  c.appendChild(imgc)

  const content = document.createElement('div')
  content.style = 'flex: 1 1 83%; min-width: 280px; text-align: left; padding-left: 2%;'
  const title = document.createElement('a')
  title.style = 'display: block; margin-bottom: 5px; text-decoration: none; color: #232424;'
  title.target = '_blank'
  title.href = n.clickUrl
  title.innerText = n.title
  content.appendChild(title)

  if (n.sponsoredBy) {
    const txt = document.createElement('span')
    txt.style = 'display: inline-block; font-size: 0.8em; color: #aaa;'
    txt.innerText = 'Ad By ' + n.sponsoredBy
    content.appendChild(txt)
  }

  c.appendChild(content)

  if (n.impressionTrackers) {
    for (const tracker of n.impressionTrackers) {
      Log.debug('Firing native imptracker ' + tracker)
      firePixel(tracker)
    }
  }

  if (n.clickTrackers) {
    imgc.onclick = title.onclick = () => {
      Log.debug('Click event, firing click trackers')
      n.clickTrackers.forEach(tracker => ajax(tracker))
    }
  }

  if (n.javascriptTrackers) {
    const trackerDiv = document.createElement('div')
    trackerDiv.innerHTML = n.javascriptTrackers
    c.appendChild(trackerDiv)
    Log.debug('Appended native js trackers')
  }

  let nativeContainer = slot.node().querySelector('.smrtb-native-container')
  if (!nativeContainer) {
    Log.debug('Native making new container')
    /* wrap all entries in a div to avoid side effects of parent styles */
    nativeContainer = document.createElement('div')
    nativeContainer.style.width = '100%'
    nativeContainer.style.padding = '10px'
    nativeContainer.style['padding-bottom'] = '0px'
    nativeContainer.style['background-color'] = '#F0F0F0'
    nativeContainer.style['box-sizing'] = 'border-box'
    nativeContainer.setAttribute('class', 'smrtb-native-container')

    const pc = document.createElement('div')
    pc.setAttribute('class', 'smrtb-promoted-by')
    pc.style = 'color: #aaa; font-size: 12px; text-align: center;'
    pc.innerText = 'PROMOTED'

    if (n.vendorIcon) {
      pc.innerText += ' BY '
      const va = document.createElement('a')
      va.target = '_blank'
      va.href = n.vendorClickUrl || 'https://smrtb.com'

      const vi = document.createElement('img')
      vi.src = n.vendorIcon
      vi.style = 'height: 12px; margin: 0px 5px; vertical-align: text-top; width: auto;'
      va.appendChild(vi)
      pc.appendChild(va)
    }

    // This holds the individual units, container encloses promoted title and units
    const wrapper = document.createElement('div')
    wrapper.appendChild(pc)
    wrapper.setAttribute('style', 'width: 100%;')
    wrapper.appendChild(nativeContainer)

    slot.node().appendChild(wrapper)
  } else {
    Log.debug('Found existing native container')
  }

  nativeContainer.appendChild(c)
}

/**
 * Accepts a prebid returned bidsBackHandler argument listing
 * each ad unit and their bids. Since our untis are all the same we will
 * flat map them in a big list here and sort them by bid price
 * so any bid may fill any available slot
 * @param units { ad-unit-1: [{ ad.ad.. width: 300,..}]}
 * @returns sorted list of all bids by price
 */
export function flatMapBids (units) {
  const bids = []

  if (!units) return bids

  for (const unit of Object.values(units)) {
    bids.push(...unit.bids)
  }
  return bids.sort((a, b) => b.cpm - a.cpm)
}

export function isVast (ad) {
  if (!ad) return false
  const s = ad.ad || ad.vastXml || ad.vastUrl
  if (!s || !s.length) return false

  switch (s.substr(0, 4).toLowerCase()) {
    case 'http':
    case '<vas':
    case '<?xm':
      return true
    default:
      return false
  }
}

/**
 * Extracts video player dimensions from a prebid media object,
 * or defaults to first banner size if available
 * @param media
 * @param ad Ad/Bid to also consider should there be any missing media info
 * @returns {*} [width, height]
 */
export function playerDims (media, ad) {
  if (!ad) {
    Log.warn('Ad object was null!')
    throw new Error('Ad object was null')
  }

  if (ad.mediaType === 'banner' || !isVast(ad)) {
    // sometimes people flag bids as video but include a script or their own player
    Log.debug('playerDims was banner ' + ad.width + 'x' + ad.height)
    return [ad.width, ad.height]
  }

  if (ad.mediaType === 'native') {
    return [1, 1]
  }

  let dims = media.video.playerSize
  if (!dims || !dims.length) {
    if (media.banner && media.banner.sizes && media.banner.sizes.length) {
      Log.debug('Couldnt find player size, falling back to banner dimensions')
      dims = media.banner.sizes[0]
    } else {
      Log.info('Couldnt find playerSize or useable banner dims, falling back to bid size')
      dims = [ad.width, ad.height]
    }
  }

  if (dims.length === 1) {
    dims = dims[0] // array of sizes
  }

  if (ad && ad.width > 1 && ad.height > 1) { // make sure not native either 1x1
    if (ad.width > dims[0]) {
      /* scales based on ad size but our test responses return wrong ad size..
      for now we will just scale down player
      Log.debug('Larger creative width than container, resizing dimensions')
      const aspectratio = (ad.width / ad.height)
      const width = dims[0]
      const height = (width / aspectratio)

      dims = [width, height]
      */
    }
  }

  Log.debug('playerDims was video using playersize ' + dims[0] + 'x' + dims[1])

  return dims
}

export function randomId () {
  const val = Math.random().toString(36).substring(2, 6) + Math.random().toString(36).substring(2, 6)
  return val
}

/**
 * Injects a script to the top most accessible window frame
 * @param src
 * @param optional frame to inject script into, or html node element to append to
 * @param attrs optional object of key:value html attributes
 * @returns {Promise<void>}
 */
export function loadScript (src, frame, attrs) {
  return new Promise((resolve, reject) => {
    const wind = frame && frame.window ? frame : topmostWindowFrame()
    const script = wind.document.createElement('script')

    script.type = 'application/javascript'
    script.src = src
    script.id = 'smrtb-import-' + randomId()
    if (attrs && typeof attrs === 'object') {
      for (const key of Object.keys(attrs)) {
        script.setAttribute(key, attrs[key])
      }
    }

    script.onload = () => setTimeout(() => resolve(script), 0)
    script.onerror = () => reject(Error('Failed to load ' + src))

    // eslint-disable-next-line no-undef
    if (frame && ((frame instanceof Element) || (frame instanceof HTMLDocument))) {
      Log.info('Attaching script to div ' + frame.id)
      frame.appendChild(script)
    } else {
      // not a specific div, attach to window
      wind.document.head.appendChild(script)
    }
  })
}

/**
 * Find a bidder object in list of bidders by code
 * @param code bidder code, e.g. 'smartrtb'
 * @param bidders list of prebid bidders
 * @param remove true if should also remove from bidders list
 * @returns {obj} bidder if present or array of bidders if multiple matches
 */
export function bidderForCode (code, bidders, remove = false) {
  const list = []
  for (const bidder of bidders) {
    if (bidder.bidder === code) {
      if (remove === true) {
        Log.debug('Removing ' + code + ' from bidders list')
        bidders.splice(bidders.indexOf(bidder), 1)
      }

      list.push(bidder)
    }
  }

  if (list.length === 0) return null
  else if (list.length === 1) return list[0]
  else return list
}

export function filterSizes (adUnits, maxDims) {
  const w = maxDims[0]
  const h = maxDims[1]

  for (const unit of adUnits) {
    const b = unit.mediaTypes.banner
    if (b && b.sizes && b.sizes.length) {
      b.sizes = b.sizes.filter(d => (d[0] <= w && d[1] <= h))

      if (b.sizes.length === 0) {
        Log.info('Disabling banner placement as too large for max dims ' + w + 'x' + h)
        delete unit.mediaTypes.banner
      }
    }

    /*
    Video player will adapt to screen size for now
    const v = unit.mediaTypes.video
    if (v && v.playerSize && v.playerSize.length) {
      let ps = v.playerSize
      if (ps.length === 1) ps = ps[0] // probably [[480, 370]]
      if (ps.length !== 2) {
        Log.warn('Cant figure out size of player: ' + JSON.stringify(v))
        continue
      }

      if (ps[0] > w || ps[1] > w) {
        Log.info('Disabling video placement as it is beyond max dims of ' + w + 'x' + h)
        delete unit.mediaTypes.video
      }
    } */

    if (!unit.mediaTypes.banner && !unit.mediaTypes.video && !unit.mediaTypes.native) {
      Log.info('No formats passed max dimensions, removing unit')
      adUnits.splice(adUnits.indexOf(unit), 1)
    }
  }

  return adUnits
}

/**
 * Checks to ensure ecpm is a valid float
 * and returns it if so, or else returns
 * default ecpm
 * @param ecpm from bidder.params.ecpm
 * @param defEcpm desired default if none supplied
 * @returns {number}
 */
export function ecpmOrDefault (ecpm, defEcpm) {
  if (ecpm === undefined || ecpm === null) {
    return defEcpm
  }

  const f = parseFloat(ecpm)
  return isNaN(f) ? defEcpm : f
}

export function shuffleArray (array) {
  for (let i = array.length - 1; i > 0; i--) {
    const rand = Math.floor(Math.random() * (i + 1));
    [array[i], array[rand]] = [array[rand], array[i]]
  }

  return array
}

/**
 * Checks if a given size exists in the array already
 * @param sizes array of sizes [[300, 250], [320, 50]]
 * @param size single size entry [300, 250]
 * @returns {boolean}
 */
export function hasSize (sizes, size) {
  for (const sz of sizes) {
    if (sz[0] === size[0] && sz[1] === size[1]) {
      return true
    }
  }

  return false
}

/**
 * Inject alt banner sizes, which includes any standard size
 * smaller than the largest existing size entry. Modifies in place.
 * @param banner media.banner which includes the .sizes[[]] property
 */
export function injectAltSizes (banner) {
  if (!banner || !banner.sizes) {
    throw Error('injectAltSizes argument not a media banner entry')
  }

  const s = banner.sizes

  let maxW = 0
  let maxH = 0
  for (const sz of s) {
    maxW = Math.max(maxW, sz[0])
    maxH = Math.max(maxH, sz[1])
  }

  const all = [
    [970, 250],
    [970, 90],
    [728, 90],
    [468, 60],
    [300, 250],
    [250, 250],
    [200, 200],
    [320, 100],
    [320, 50],
    [300, 50],
    [300, 600],
    [160, 600],
    [120, 600]
  ]

  for (const sz of all) {
    if (sz[0] <= maxW && sz[1] <= maxH && !hasSize(s, sz)) {
      Log.debug('Adding alt size ' + sz[0] + 'x' + sz[1])
      s.push(sz)
    }
  }
}

/**
 * @returns {string} attempts to gather and return the top most current
 * page url
 */
export function getTopUrl () {
  let url = window.location.href
  try {
    url = window.top.location.href
  } catch (e) {
    Log.warn('In xdomain frame, cant get proper url')
  }

  return url
}

/**
 * @returns {string|*} top level domain of current site with protocol
 * e.g. https://example.com
 */
export function getTopDomain () {
  if ('ancestorOrigins' in window) {
    return window.location.ancestorOrigins.pop()
  }

  return window.location.origin
}

/**
 * @returns {boolean} true if web page is secure
 */
export function isSecure () {
  return window.location.protocol === 'https:'
}

/**
 * Transforms an object into a series of get parameters
 * @param params object to convert
 * @returns {string} query string, no ? included. e.g. a=1&b=2&c=3
 */
export function objToQuery (params) {
  return Object.keys(params).map(key => {
    if (params[key] !== null) {
      return key + '=' + encodeURIComponent(params[key])
    }
  }).join('&')
}

/**
 * Will load a vast url and return the tag XML if at least one ad is present.
 * @param tag url or vast tag
 * @returns {string} VAST markup or null if no ads available
 */
export function loadVast (tag) {
  return new Promise((resolve, reject) => {
    const scheme = tag.substr(0, 2)
    if (scheme.toLowerCase() !== 'ht' || scheme !== '//') {
      Log.debug('loadVast content appears to be raw vast tag: ' + tag)
      return resolve(tag)
    }

    ajax(tag).then(res => {
      if (!res || !res.length) {
        return resolve(null)
      }

      Log.debug(res)

      /* global DOMParser */
      const tag = new DOMParser().parseFromString(res, 'text/xml')
      Log.debug(tag)
      if (tag.getElementsByTagName('Ad').length > 0) {
        /* global XMLSerializer */
        return resolve(new XMLSerializer().serializeToString(tag))
      }

      resolve(null)
    }).catch(reject)
  })
}

/**
 * Checks if tag is running in a frame (such as a GPT)
 * @returns {object} with properties crossDomain (t/f), inFrame (t/f), and frameId of parent if applicable
 */
export function inFrame () {
  const res = {
  }

  try {
    res.crossDomain = (document.location.hostname !== window.top.location.hostname)
    res.inFrame = (window.self !== window.top)
    res.frameId = window.frameElement ? window.frameElement.id : null
  } catch (e) {
    Log.info('Caught error checking for frame, is xdomain')
    res.crossDomain = true
    res.inFrame = true
    res.frameId = null
  }

  return res
}

/**
 * Extracts the primary bidder code from a bidder name,
 * even if external. E.g. bidder_external returns 'bidder'
 * @param bidder bidder code
 * @returns {string}
 */
export function bidderCode (bidder) {
  return bidder.split('_')[0]
}

/**
 * @returns {Window} The top most accessible window frame avaialble
 */
export function topmostWindowFrame () {
  const frame = inFrame()
  const wind = (!frame.inFrame || (frame.inFrame && !frame.crossDomain)) ? window.top : window
  if (wind === wind.top) {
    Log.debug('Got window.top!')
  } else {
    Log.debug('Couldnt access window.top, had to use current frame')
  }

  return wind
}

export function parseXml (xml) {
  if (typeof window.DOMParser !== 'undefined') {
    return new window.DOMParser().parseFromString(xml, 'application/xml')
  } else if (typeof window.ActiveXObject !== 'undefined' &&
    new window.ActiveXObject('Microsoft.XMLDOM')) {
    const doc = new window.ActiveXObject('Microsoft.XMLDOM')
    doc.async = 'false'
    doc.loadXML(xml)
    return doc
  } else {
    throw new Error('No XML parser found')
  }
}

function getTagValue (doc, tag) {
  const nodes = doc.getElementsByTagName(tag)
  if (!nodes || !nodes.length) return

  return nodes[0].innerHTML
}

export function rssReader (link) {
  return new Promise((resolve, reject) => {
    Log.debug('Reading RSS url ' + link)
    if (!link || (link.indexOf('http') !== 0 && link.indexOf('//') !== 0)) {
      Log.warn('rssReader called with bad link value ' + link)
      resolve([])
      return
    }

    ajax(link).then(data => {
      Log.debug(data)
      const xml = parseXml(data)
      const items = xml.getElementsByTagName('item')
      if (items.length === 0) {
        Log.warn('No video items found in RSS feed')
        resolve([])
        return
      }

      return Object.values(items).map(item => {
        const title = getTagValue(item, 'title')
        const link = getTagValue(item, 'link')
        let media = null

        const mv = item.getElementsByTagName('media:content')
        if (mv) {
          const mvn = mv[0]
          console.log(mvn)
          media = mvn.getAttribute('url')
        }

        resolve({
          title,
          link,
          media
        })
      })
    })
  })
}

export function deepMerge (...objects) {
  const isObject = obj => obj && typeof obj === 'object'

  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach(key => {
      const pVal = prev[key]
      const oVal = obj[key]

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = pVal.concat(...oVal)
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = deepMerge(pVal, oVal)
      } else {
        prev[key] = oVal
      }
    })

    return prev
  }, {})
}

export function firePixel (src) {
  const img = document.createElement('img')
  img.width = 1
  img.height = 1
  img.style = 'display: block; height: 1px; width: 1px; margin: 0px;'
  img.src = src

  document.body.appendChild(img)
}

export function getCmp () {
  return new Promise((resolve, reject) => {
    if (!('__cmp' in window)) {
      Log.debug('No cmp found')
      return resolve(null)
    }

    if (!('getConsentData' in window.__cmp)) {
      Log.warn('CMP exists but does not support getConsentData!')
      return resolve(null)
    }

    Log.debug('Pinging CMP')
    window.__cmp('ping', null, d => {
      Log.debug('CMP responded to ping')
      if (d && d.cmpLoaded) {
        Log.info('CMP ready, requesting consent data')
        window.__cmp('getConsentData', null, d => {
          Log.debug('Got cmp data')
          resolve(d)
        })
      } else {
        Log.warn('CMP wasnt ready!')
        return resolve(null)
      }
    })
  })
}

export function getCcpa () {
  return new Promise((resolve, reject) => {
    if (!('__uspapi' in window)) {
      return resolve(null)
    }

    if (!('getUSPData' in window.__uspapi)) {
      Log.warn('CCPA window api exists but doesnt support getUSPData method')
      return resolve(null)
    }

    window.__uspapi('getUSPData', 1, d => {
      Log.debug('Got ccpa data')
      resolve(d)
    })
  })
}

/**
 * Add aliases to prebid bidders in list
 * @param rc remoteConfig
 */
export function aliasBidders (rc) {
  Log.debug('Aliasing bidders')
  console.log(rc.bidders())

  const map = { /* bidder: numidx occurances */ }

  for (const bidder of rc.bidders()) {
    if (map[bidder.bidder] === undefined) {
      map[bidder.bidder] = 0
    } else {
      map[bidder.bidder] = (map[bidder.bidder] + 1)
    }

    const idx = map[bidder.bidder]
    if (bidder.params && !('bidder' in bidder.params) && idx > 0) {
      bidder.bidder = bidder.bidder + '_' + idx

      Log.debug('Adding local alias for ' + bidder.bidder)
    }
  }
}

/**
 * Remove alias from bidder name e.g. bidder_1 -> bidder
 * handles if bidder adapter already an underscore in it or not
 */
export function unalias (bidder) {
  const p = bidder.split('_')
  if (p.length === 1 || bidder.lastIndexOf('_') !== bidder.length - 2) {
    return bidder
  } else {
    return p.slice(0, -1).join('_')
  }
}

/**
 * Check if div has any child content
 * @return {boolean} true if empty
 */
export function divIsEmpty (div) {
  return div.childElementCount === 0
}
